import { Calendar, Clock, CalendarDays } from "lucide-react";
import React from "react";

import type { MapTimeFilter } from "@/types/calendar";

interface MapTimeFiltersProps {
  timeFilter: MapTimeFilter;
  onFilterChange: (filter: MapTimeFilter) => void;
  eventCount: number;
  isLoading?: boolean;
}

export const MapTimeFilters: React.FC<MapTimeFiltersProps> = ({
  timeFilter,
  onFilterChange,
  eventCount,
  isLoading = false,
}) => {
  const filters: {
    value: MapTimeFilter;
    label: string;
    icon: React.ReactNode;
  }[] = [
    {
      value: "today",
      label: "Today",
      icon: <Clock className="h-4 w-4" />,
    },
    {
      value: "week",
      label: "This Week",
      icon: <Calendar className="h-4 w-4" />,
    },
    {
      value: "month",
      label: "This Month",
      icon: <CalendarDays className="h-4 w-4" />,
    },
  ];

  return (
    <section
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
      aria-labelledby="time-filter-heading"
    >
      <div className="flex items-center justify-between mb-3">
        <h3
          className="text-sm sm:text-base font-semibold text-gray-900"
          id="time-filter-heading"
        >
          Time Range
        </h3>
        <span
          className="text-xs sm:text-sm text-gray-500"
          aria-live="polite"
          aria-atomic="true"
        >
          {isLoading
            ? "Loading..."
            : `${eventCount} event${eventCount !== 1 ? "s" : ""}`}
        </span>
      </div>

      <div
        className="grid grid-cols-3 gap-2"
        role="group"
        aria-label="Time range filter options"
      >
        {filters.map(({ value, label, icon }) => {
          const isActive = timeFilter === value;
          return (
            <button
              key={value}
              onClick={() => onFilterChange(value)}
              className={`
                flex flex-col items-center justify-center p-3 sm:p-4 rounded-lg
                transition-all duration-200 border-2
                min-h-[60px] sm:min-h-[72px] touch-manipulation
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500
                ${
                  isActive
                    ? "bg-gradient-to-br from-indigo-600 to-purple-600 text-white border-transparent shadow-md transform scale-105"
                    : "bg-white text-gray-700 border-gray-200 hover:border-indigo-300 hover:bg-gray-50"
                }
                ${isLoading ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
              `}
              disabled={isLoading}
              aria-pressed={isActive}
              aria-label={`${label} - ${getDateRangeText(value)}`}
              aria-disabled={isLoading}
            >
              <div
                className={`mb-1 ${isActive ? "text-white" : "text-gray-500"}`}
                aria-hidden="true"
              >
                {icon}
              </div>
              <span className="text-xs sm:text-sm font-medium">{label}</span>
            </button>
          );
        })}
      </div>

      {/* Date range display with better accessibility */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <p
          className="text-xs sm:text-sm text-gray-500 text-center"
          aria-live="polite"
        >
          {getDateRangeText(timeFilter)}
        </p>
      </div>
    </section>
  );
};

/**
 * Get human-readable date range text
 */
const getDateRangeText = (filter: MapTimeFilter): string => {
  const today = new Date();
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
  };

  switch (filter) {
    case "today":
      return today.toLocaleDateString("en-US", { weekday: "long", ...options });

    case "week": {
      const weekEnd = new Date(today);
      weekEnd.setDate(weekEnd.getDate() + 6);
      return `${today.toLocaleDateString("en-US", options)} - ${weekEnd.toLocaleDateString("en-US", options)}`;
    }

    case "month": {
      const monthEnd = new Date(today);
      monthEnd.setMonth(monthEnd.getMonth() + 1);
      return `${today.toLocaleDateString("en-US", options)} - ${monthEnd.toLocaleDateString("en-US", options)}`;
    }

    default:
      return "";
  }
};

export default MapTimeFilters;
